import {ActionIcon, Badge, Button, Checkbox, Group, TextInput, Title} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconX} from '@tabler/icons-react'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {useCurrentUser} from '../auth/use-current-user'
import {useDispatchYards} from '../dispatch/use-dispatch'
import {userAccountStatusColor} from '../theme/semantic-colors'
import type {UserAccount} from './schemas'
import {USER_ROLE_LABELS} from './schemas'
import {useCreateUser, useUpdateUser, useUsers} from './use-users'
import {UserFormModal} from './user-form-modal'

function hasActiveFilters(name: string) {
    return Boolean(name.trim())
}

function filterUsers(users: UserAccount[], nameFilter: string, includeInactive: boolean) {
    const name = nameFilter.trim().toLowerCase()

    return users.filter(
        (user) =>
            (includeInactive || user.deactivatedAt == null) &&
            (!name ||
                user.name.toLowerCase().includes(name) ||
                user.email.toLowerCase().includes(name)),
    )
}

function compareUsers(
    left: UserAccount,
    right: UserAccount,
    columnAccessor: DataTableSortStatus<UserAccount>['columnAccessor'],
) {
    if (columnAccessor === 'status') {
        const leftValue = left.deactivatedAt == null ? 'active' : 'deactivated'
        const rightValue = right.deactivatedAt == null ? 'active' : 'deactivated'
        return leftValue.localeCompare(rightValue)
    }

    const leftValue = String(left[columnAccessor as keyof UserAccount] ?? '')
    const rightValue = String(right[columnAccessor as keyof UserAccount] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortUsers(users: UserAccount[], sortStatus: DataTableSortStatus<UserAccount>) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...users].sort(
        (left, right) => directionMultiplier * compareUsers(left, right, sortStatus.columnAccessor),
    )
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function UsersPage() {
    const currentUser = useCurrentUser()
    const [nameFilter, setNameFilter] = useState('')
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const [includeInactive, setIncludeInactive] = useState(false)
    const {data: users = [], isLoading} = useUsers()
    const {data: yards = []} = useDispatchYards()
    const createUser = useCreateUser()
    const updateUser = useUpdateUser()
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<UserAccount>>({
        columnAccessor: 'name',
        direction: 'asc',
    })
    const [modalState, setModalState] = useState<
        {open: false} | {open: true; mode: 'create'} | {open: true; mode: 'edit'; user: UserAccount}
    >({open: false})

    const filtersActive =
        hasActiveFilters(debouncedName) ||
        (!includeInactive && users.some((user) => user.deactivatedAt != null))

    const sortedUsers = useMemo(
        () => sortUsers(filterUsers(users, debouncedName, includeInactive), sortStatus),
        [users, debouncedName, includeInactive, sortStatus],
    )

    const columns = useMemo((): DataTableColumn<UserAccount>[] => {
        return [
            {
                accessor: 'name',
                title: 'Name',
                sortable: true,
                filter: (
                    <TextInput
                        label="Name or email"
                        description="Show users whose name or email includes the specified text"
                        placeholder="Search users..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
            {
                accessor: 'email',
                title: 'Email',
                sortable: true,
            },
            {
                accessor: 'role',
                title: 'Role',
                sortable: true,
                render: (row) =>
                    row.isSalesRep && row.role === 'member'
                        ? `${USER_ROLE_LABELS[row.role]} · Sales rep`
                        : USER_ROLE_LABELS[row.role],
            },
            {
                accessor: 'dispatchYardId',
                title: 'Yard',
                render: (row) =>
                    yards.find((yard) => yard.id === row.dispatchYardId)?.name ?? 'No yard',
            },
            {
                accessor: 'status',
                title: 'Status',
                sortable: true,
                filter: (
                    <Checkbox
                        label="Include inactive"
                        checked={includeInactive}
                        onChange={(event) => setIncludeInactive(event.currentTarget.checked)}
                    />
                ),
                filtering: includeInactive,
                render: (row) =>
                    row.deactivatedAt == null ? (
                        <Badge
                            color={userAccountStatusColor(row.deactivatedAt == null)}
                            variant="light"
                        >
                            Active
                        </Badge>
                    ) : (
                        <Badge
                            color={userAccountStatusColor(row.deactivatedAt == null)}
                            variant="light"
                        >
                            Deactivated
                        </Badge>
                    ),
            },
            {
                accessor: 'actions',
                title: 'Actions',
                textAlign: 'right',
                render: (row) => (
                    <Button
                        variant="subtle"
                        size="compact-sm"
                        onClick={() => setModalState({open: true, mode: 'edit', user: row})}
                    >
                        Edit
                    </Button>
                ),
            },
        ]
    }, [includeInactive, nameFilter, yards])

    return (
        <>
            <Group justify="space-between" mb="md">
                <Title order={1}>Users</Title>
                <Button onClick={() => setModalState({open: true, mode: 'create'})}>
                    Add user
                </Button>
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                idAccessor="id"
                records={sortedUsers}
                columns={columns}
                fetching={isLoading}
                minHeight={sortedUsers.length === 0 ? 180 : undefined}
                noRecordsText={filtersActive ? 'No users match your filters.' : 'No users yet.'}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
            />

            <UserFormModal
                opened={modalState.open}
                user={modalState.open && modalState.mode === 'edit' ? modalState.user : undefined}
                currentUserId={currentUser?.id}
                onClose={() => setModalState({open: false})}
                onCreate={(input) => createUser.mutateAsync(input)}
                onUpdate={(userId, input) => updateUser.mutateAsync({userId, input})}
                onSaved={() => setModalState({open: false})}
            />
        </>
    )
}
